import http from "http";
import https from "https";

import axios, { AxiosRequestConfig, AxiosError, AxiosInstance, AxiosResponse } from "axios";
import qs from "qs";
import _ from "lodash";

import { LanguageCode } from "../common/enums";
import { CtxMountedType } from "../common/types";
import { isBrowser } from "../common/utils";

import { IoOptions, IoHooks, IoResponse } from "./types";
import { translate } from "./i18n";
import {
  isValidResponse,
  createErrorResponse,
  formatRequestConfig,
  formatResponse,
  prettyLog,
  checkAndAppendResponseSetCookie,
} from "./utils";

export { IoResponse };

const httpAgent = isBrowser ? null : new http.Agent({ keepAlive: true });
const httpsAgent = isBrowser
  ? null
  : new https.Agent({ keepAlive: true, rejectUnauthorized: false }); // 测试环境证书可能自签名，不检查合法性

// 私有属性的 Key 定义
// 存放 axios 的实例
const AXIOS = Symbol("services/IO/#AXIOS");
// 用于定义请求方法
const REQUEST = Symbol("services/IO/#request");
// 存放请求额外参数字段，用于当批量请求需要添加请求头等额外字段时，注意：当不需要用到时及时清空
const EXTRA_REQUEST_OPTIONS = Symbol("services/IO/#extraOptions");

const timeout = 60 * 1000; // framework 与 业务站点的公共超时时间，可根据实际情况调整。实际 SSR 在整体响应时间过长时会自动降级

export default class IO {
  ctx: CtxMountedType;

  [AXIOS]: AxiosInstance;

  axiosInstance: AxiosInstance | undefined;

  [EXTRA_REQUEST_OPTIONS]: AxiosRequestConfig;

  constructor({ ctx, config, events, needRevealAxiosInstance }: IoOptions) {
    this.ctx = _.cloneDeep(ctx);
    this[EXTRA_REQUEST_OPTIONS] = {};
    // 初始化 axios 实例
    this[AXIOS] = axios.create({
      httpAgent,
      httpsAgent,
      // 留意，由于后端网关未做统一抽象，使用请求 host 作为 baseURL(以浏览器的身份请求)
      // 依赖于各层转发正确传递了 host 信息。生产环境，应当直接请求至生产域名，如 http://mall.alukalu.com

      // browser 端，总是请求当前域的 /fapi。由 /fapi 转发网关
      // server 端，总是回环到当前站点的 nginx
      //          除非后端已拆分如 app.akulaku.com 的独立网关，不再依赖站点 nginx 做分发
      baseURL:
        isBrowser || ctx.lifecycleStatus.isExportMode || !ctx.req
          ? ""
          : `${ctx.req.protocol}://${ctx.req.headers.host}`,
      timeout,
      ...config,
      headers: {
        "language-id": ctx.locale.languageId,
        "country-id": ctx.locale.countryId,
        ...(config ? config.headers : undefined),
        ...(!isBrowser && ctx.req
          ? _.pick(ctx.req.headers, ["user-agent", "cookie", "referer"])
          : undefined),
      },
    });
    // 请求拦截器
    this[AXIOS].interceptors.request.use(
      (config) => {
        // @ts-expect-error 统计请求耗时
        config._ts = Date.now();
        // @ts-expect-error 项目或者框架层自定义的属性
        switch (config.dataType) {
          case "json":
          case undefined:
            //如果data为plain object axios默认按照json处理，暂不处理;
            config.headers[config.method as string]["Content-Type"] =
              "application/json;charset=utf-8";
            break;
          case "url":
            //axios已默认Content-Type为application/x-www-form-urlencoded
            config.headers[config.method as string]["Content-Type"] =
              "application/x-www-form-urlencoded;charset=utf-8";
            config.data = qs.stringify(config.data);
            break;
          default:
            break;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // 返回拦截器
    this[AXIOS].interceptors.response.use(
      (response) => {
        // @ts-expect-error 统计请求耗时
        response.config._ts = Date.now() - response.config._ts;

        if (!isBrowser && this.ctx.res) {
          this.ctx.res.append(
            "X-AL-IO-HTTP-Info",
            [
              response.status,
              response.request.path.replace(/^\w+:\/\/.+?\//, "/").replace(/\?.*$/, ""),
              // @ts-expect-error 统计请求耗时
              `${response.config._ts}ms`,
            ].join(" ")
          );
        }

        // 检查请求上携带的 set-cookie，应用到当前页面请求上
        checkAndAppendResponseSetCookie(ctx, response);

        prettyLog({
          message: "response",
          // @ts-expect-error 统计请求耗时
          time: response.config._ts,
          requestInfo: response.config ? formatRequestConfig(response.config) : " ",
          responseInfo: formatResponse(response),
          logLevel: "debug",
        });
        return response;
      },
      (error) => {
        if (!error.config) {
          return Promise.reject(error);
        }
        // 统计请求耗时
        error.config._ts = Date.now() - error.config._ts;

        if (!isBrowser && this.ctx.res) {
          this.ctx.res.append(
            "X-AL-IO-HTTP-Info",
            [
              (error.response && error.response.status) || "",
              ((error.request && error.request.path) || "")
                .replace(/^\w+:\/\/.+?\//, "/")
                .replace(/\?.*$/, ""),
              `${error.config._ts}ms`,
            ].join(" ")
          );
        }

        // 检查请求上携带的 set-cookie，应用到当前页面请求上
        if (error && error.response) {
          checkAndAppendResponseSetCookie(ctx, error.response);
        }

        if (events && events.onResponseError) {
          events.onResponseError(error);
        }

        const isUserDetail = /\b\/user\/basic\/get\b/.test(error.config.url);

        prettyLog({
          message: error.message,
          time: error.config._ts,
          requestInfo: error.config ? formatRequestConfig(error.config) : " ",
          responseInfo: error.response ? formatResponse(error.response) : " ",
          logLevel: isUserDetail ? "debug" : "warn",
        });
        return Promise.reject(error);
      }
    );

    if (needRevealAxiosInstance) {
      this.axiosInstance = this[AXIOS];
    }
  }

  /**
   * 用于发起 get 请求
   * @param {string} url 请求地址
   * @param {object} data 请求数据
   * @param {object} options 请求其他参数设置
   */
  get<T>(
    url: string,
    data?: Record<string, any>,
    options?: AxiosRequestConfig
  ): Promise<IoResponse<T>> {
    if (!url || url === "") {
      throw Error("url必填");
    }
    return this[REQUEST]<T>(
      "get",
      {
        url,
        data,
      },
      options
    );
  }

  /**
   * 用于发起 post 请求
   * @param {string} url 请求地址
   * @param {object} data 请求数据
   * @param {object} options 请求其他参数设置
   */
  post<T>(
    url: string,
    data: Record<string, any>,
    options?: AxiosRequestConfig
  ): Promise<IoResponse<T>> {
    if (!url || url === "") {
      throw Error("url必填");
    }

    return this[REQUEST]<T>(
      "post",
      {
        url,
        data: data || {},
      },
      options
    );
  }

  /**
   * 用于请求第三方接口，保留是为了兼容以前代码
   * @param {'post'|'get'} method 请求方式
   * @param {string} url 请求地址
   * @param {object} data 请求数据
   * @param {object} options 请求其他参数设置
   */
  async thirdParty<T>(
    method: string,
    url: string,
    data: Record<string, any>,
    options?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    // 由于第三方对错误的返回多样，比如http code/response等，建议在调用thirdParty时使用try/catch包裹，在业务模块中自定义错误捕捉
    if (!url || url === "" || !method) {
      throw Error("method&url必填");
    }

    return await this[AXIOS].request<T>({
      method,
      url,
      ...(method === "get" ? { params: data } : { data }),
      ...options,
    });
  }

  /**
   * 用于请求第三方接口，保留是为了兼容以前嗲吗
   * @param {'post'|'get'} method 请求方式
   * @param {string} url 请求地址
   * @param {object} data 请求数据
   * @param {object} options 请求其他参数设置
   */
  async [REQUEST]<T>(
    method: string,
    { url, data }: { url: string; data?: Record<string, any> },
    options = {}
  ): Promise<IoResponse<T>> {
    // 添加额外扩展参数，用于当批量请求需要添加请求头等额外字段时
    options = _.merge(null, this[EXTRA_REQUEST_OPTIONS], options);
    try {
      // 发起请求
      const res = await this[AXIOS].request<IoResponse<T>>({
        method,
        url,
        ...(method === "get" ? { params: data } : { data }),
        ..._.pick(options, ["timeout", "headers", "dataType"]),
      });
      // 转换数据
      return IO.convertAxiosResponseToSizeResponse<T>({ success: true, resOrError: res }, this.ctx);
    } catch (err) {
      // 转换数据
      return IO.convertAxiosResponseToSizeResponse<T>(
        { success: false, resOrError: err as AxiosError },
        this.ctx
      );
    }
  }

  /**
   * 处理请求返回结果
   * @param {object} param0
   * @param {object} ctx
   */
  static convertAxiosResponseToSizeResponse<T>(
    {
      success,
      resOrError,
    }: { success: boolean; resOrError: AxiosResponse<IoResponse<T>> | AxiosError },
    ctx: CtxMountedType
  ): IoResponse<T> {
    const languageCode = ctx.locale.languageCode as LanguageCode;
    if (success) {
      let resData = (resOrError as AxiosResponse).data;
      if (!isValidResponse(resData)) {
        resData = createErrorResponse("FE.4", "Invalid response");
      }

      // 字段缺失时补充字段
      [
        ["errCode", ""],
        ["errMsg", ""],
        ["data", null],
      ].forEach(([key, defaultVal]) => {
        if (!((key as string) in resData)) {
          resData[key as string] = defaultVal;
        }
      });

      if (!resData.success) {
        // 设计功能为：
        // 1. 产品文档有要求的文案，不展示错误码
        // 2. 未定义的文案，可以展示错误码，尤其是系统异常等
        // 实际业务不可能遍历所有可能的文案，只能是尽可能不展示错误码，除非明确确定是非业务异常
        resData.$rawErrMsg = resData.errMsg;
        resData.errMsg =
          resData.errMsg ||
          `${translate(languageCode, "系统异常，请稍后重试")}(${resData.errCode})`;
      }

      return resData;
    } else {
      // 将 http 异常转义，视为特定形式的业务异常，抹平前后端的通信或约定差异
      // 对于特定错误类型，如 网络中断、系统维护 等，使用特定的状态码标识
      const error = resOrError as AxiosError;
      if (error.response) {
        // 历史约定，基于错误码划分接口行为。事实上不符合接口行为
        if (error.response.status === 401) {
          // 未登录的时候调用onUnauthorized函数
          if (this.hooks.onUnauthorized) {
            this.hooks.onUnauthorized();
          }
          return createErrorResponse("FE.1", "Unauthorized", error.response.status);
        } else if (error.response.status >= 400) {
          const errMsg = `${translate(languageCode, "系统异常，请稍后重试")}(${
            error.response.status || "FE.2"
          })`;
          return createErrorResponse("FE.2", errMsg, error.response.status);
        } else {
          const errMsg = `${translate(languageCode, "系统异常，请稍后重试")}(${
            error.response.status || "FE.3"
          })`;
          return createErrorResponse("FE.3", errMsg, error.response.status);
        }
      } else {
        if (!isBrowser) {
          // 服务端，接口超时等无法确认响应状态的，直接抛出异常
          // const throwError = new Error(`service gateway error(${resOrError.message})`)
          // throwError.statusCode = 504
          // throw throwError
          const errMsg = `${translate(languageCode, "系统异常，请稍后重试")}(FE.5)`;
          return createErrorResponse("FE.5", errMsg);
        } else {
          if (error.request && error.request.status === 0) {
            // 网络错误，无网络或网络被断开
            const errMsg = translate(languageCode, "网络异常，稍后重试");
            return createErrorResponse("FE.0", errMsg);
          } else {
            const errMsg = `${translate(languageCode, "系统异常，请稍后重试")}(FE.9)`;
            return createErrorResponse("FE.9", errMsg);
          }
        }
      }
    }
  }

  static hooks: IoHooks = {};

  // 设置额外参数
  setExtraOptions(options: AxiosRequestConfig): void {
    this[EXTRA_REQUEST_OPTIONS] = options || {};
  }
}
